import { useCallback, useContext, useRef, Dispatch, SetStateAction, MutableRefObject } from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import agent from "../../../services/api";
import { useRuleBasedInboxStore } from "../../../store/ruleBasedInboxStore";
import { useVoiceCallStore } from "../../../store/voiceCallStore";
import { UserContext, UserContextPayload } from "../../../context/userContext";
import { IntegrationContext, IntegrationContextPayload } from "../../../context/integrationContext";
import {
    currentActiveChatbotThreadTypeNew,
    newThreadInfo,
} from "../../../models/axiosModel";
import { CHAT_MESSAGES_LIMIT, MANUAL_OVERRIDE_BTN_TEXT, METADATA_KEYS } from "../constants";
import {
    formatPhoneNumber,
    getManualOverrideConfig,
    getMetadataValue,
    getThreadInstagramUserName,
    mapThreadExecutionToChatMessages,
    updateChatbotUserIdParam,
} from "../utils";

/**
 * Scrolls #chatUI to the bottom immediately, then keeps it there while
 * late-loading content (images, videos, embeds) finishes rendering.
 *
 * ── Why img `load` events alone are unreliable ──────────────────────────────
 * Browsers serve cached images synchronously during layout.  By the time JS
 * checks `img.complete`, a disk-cached image may have already resolved — but
 * its layout height might not yet be committed, so `scrollHeight` still reads
 * the pre-image value.  Attaching a `load` listener is then a no-op because
 * the event already fired before we could listen.
 *
 * ── The ResizeObserver approach ─────────────────────────────────────────────
 * ResizeObserver fires *after* the browser has finished computing layout and
 * *before* it paints the frame.  This means:
 *   • By the time the callback runs, `scrollHeight` already reflects the
 *     newly-rendered image heights.
 *   • Setting `scrollTop = scrollHeight` inside the callback guarantees the
 *     user sees the correct bottom position on the very next paint.
 *
 * We observe the *direct children* of chatUI (one element per message group).
 * When any child grows (image or other media finishes loading), the child's
 * bounding-box changes, ResizeObserver fires, and we re-scroll.
 *
 * A MutationObserver watches for children added by React's deferred rendering
 * so they also get observed immediately.
 *
 * ── Stop conditions ─────────────────────────────────────────────────────────
 *   • User intentionally scrolls up more than 80 px from the bottom → stop.
 *   • 8 seconds elapse (safety net; all media should be done by then) → stop.
 *
 * Returns a cancel function.  Call it when switching threads so the old pin
 * cannot interfere with the incoming thread's scroll state.
 */
function keepScrollPinnedToBottom(chatUI: HTMLElement): () => void {
    let pinned = true;

    const scrollToBottom = () => {
        if (!pinned) return;
        chatUI.scrollTop = chatUI.scrollHeight;
    };

    // ── Scroll to bottom immediately ──────────────────────────────────────
    scrollToBottom();

    // ── ResizeObserver: re-scroll whenever any message element grows ──────
    // ResizeObserver.observe() is idempotent — safe to call multiple times on
    // the same element (subsequent calls are no-ops or update the box model).
    const resizeObs = new ResizeObserver(scrollToBottom);
    Array.from(chatUI.children).forEach((child) => resizeObs.observe(child));

    // ── MutationObserver: observe children added by deferred React renders ─
    const mutObs = new MutationObserver(() => {
        // Observe any newly-added children so their future size changes are tracked
        Array.from(chatUI.children).forEach((child) => resizeObs.observe(child));
        // Re-scroll in case content was added below the current viewport
        scrollToBottom();
    });
    mutObs.observe(chatUI, { childList: true }); // childList only — no subtree needed

    // ── Scroll listener: stop pinning when the user scrolls up on purpose ─
    const onScroll = () => {
        if (!pinned) return;
        const atBottom =
            chatUI.scrollTop + chatUI.clientHeight >= chatUI.scrollHeight - 80;
        if (!atBottom) stopPinning();
    };
    chatUI.addEventListener("scroll", onScroll, { passive: true });

    // ── Safety-net timer ──────────────────────────────────────────────────
    const timer = setTimeout(stopPinning, 8000);

    function stopPinning() {
        clearTimeout(timer);
        pinned = false;
        resizeObs.disconnect();
        mutObs.disconnect();
        chatUI.removeEventListener("scroll", onScroll);
    }

    return stopPinning;
}

type GetSourceDetails = (
    ipAddress: string,
    chatbot_domain: string,
    source: string,
    phone_number: string | null,
    user_name: string | null,
    last_message_at: string,
    city: string,
    country: string,
    chatbot_user_input_token: number,
    chatbot_user_output_token: number,
    openai_assistant_id: string,
    openai_thread_id: string,
    waba_name: string | null,
    waba_phone_number: string | null,
    flag_azure?: boolean,
    instagram_account_name?: string | null,
    instagram_account_id?: string | null,
    insta_name?: string | null,
    insta_user_name?: string | null
) => void;

interface UseGetSpecificChatParams {
    getSourceDetails: GetSourceDetails;
    setIsLoadingChat: Dispatch<SetStateAction<boolean>>;
    scrollPinCleanupRef: MutableRefObject<(() => void) | null>;
}

export type GetSpecificChat = (
    chatbot_user_id: number | string,
    offSetValue: number,
    flagDebugParam: boolean,
    flagViewDetailedLogParam: boolean,
    flagGetThreadDetailByPhoneNumber?: boolean,
    phoneNumberInfo?: newThreadInfo | null,
    forceRefresh?: boolean
) => Promise<void>;

export const useGetSpecificChat = ({
    getSourceDetails,
    setIsLoadingChat,
    scrollPinCleanupRef,
}: UseGetSpecificChatParams) => {
    const { userDetail, handleError } = useContext(UserContext) as UserContextPayload;
    const { integrationListNewStructure } = useContext(IntegrationContext) as IntegrationContextPayload;
    const [searchParams, setSearchParams] = useSearchParams();

    const { setIsVoiceCallingEnableWaba, setCallingWabaDetails, setCallingPersonDetails } = useVoiceCallStore(
        useShallow((state) => ({
            setIsVoiceCallingEnableWaba: state.setIsVoiceCallingEnableWaba,
            setCallingWabaDetails: state.setCallingWabaDetails,
            setCallingPersonDetails: state.setCallingPersonDetails,
        }))
    );

    const {
        setCurrentActiveChatbotThread,
        setSourceDetails,
        setOffset,
        setFlagHasMoreMessagesToLoad,
        setFlagLoadingPreviousMessages,
        setChatClicked,
        getWabaNameAndPhoneNumberBytIntegratedChannelId,
        getIntegratedChannelData,
        needToShowViewDetailedLogBtn,
        checkIfChatIsExpiredForLeaveBtn,
        triggerAttchmentIconHeartbeat,
        setSelectedThreadId,
        setSelectedThread,
        getThreadFromCache,
        setThreadCache,
        getThreadScrollPosition,
        setNewThreadInfo,
        setFlagRenderNewConversation,
        getInstaAccountNameAndIdByIntegratedChannelId,
        setIsInfoTabLoading
    } = useRuleBasedInboxStore(
        useShallow((state) => ({
            setCurrentActiveChatbotThread: state.setCurrentActiveChatbotThread,
            setSourceDetails: state.setSourceDetails,
            setOffset: state.setOffset,
            setFlagHasMoreMessagesToLoad: state.setFlagHasMoreMessagesToLoad,
            setFlagLoadingPreviousMessages: state.setFlagLoadingPreviousMessages,
            setChatClicked: state.setChatClicked,
            getWabaNameAndPhoneNumberBytIntegratedChannelId: state.getWabaNameAndPhoneNumberBytIntegratedChannelId,
            getIntegratedChannelData: state.getIntegratedChannelData,
            needToShowViewDetailedLogBtn: state.needToShowViewDetailedLogBtn,
            checkIfChatIsExpiredForLeaveBtn: state.checkIfChatIsExpiredForLeaveBtn,
            triggerAttchmentIconHeartbeat: state.triggerAttchmentIconHeartbeat,
            setSelectedThreadId: state.setSelectedThreadId,
            setSelectedThread: state.setSelectedThread,
            getThreadFromCache: state.getThreadFromCache,
            setThreadCache: state.setThreadCache,
            getThreadScrollPosition: state.getThreadScrollPosition,
            setNewThreadInfo: state.setNewThreadInfo,
            setFlagRenderNewConversation: state.setFlagRenderNewConversation,
            getInstaAccountNameAndIdByIntegratedChannelId: state.getInstaAccountNameAndIdByIntegratedChannelId,
            setIsInfoTabLoading: state.setIsInfoTabLoading
        }))
    );

    const handleErrorForGetThreadDetailByPhoneNumber = useCallback((response: any, phoneNumberInfo: newThreadInfo | null) => {
        // Error Static Code for Error: The thread has been deleted or no longer exists.
        // If thread is not exist or delete, then create a new thread view. Otherwise handle error
        if (response?.error?.code == 9247) {
            setCurrentActiveChatbotThread(null);
            setNewThreadInfo(phoneNumberInfo);
            setFlagRenderNewConversation(true);
            setSourceDetails({});

            setSelectedThreadId(null);
            setSelectedThread(null);
            setSearchParams(updateChatbotUserIdParam(null));
        } else {
            handleError(response);
        }
    }, [
        setCurrentActiveChatbotThread,
        setNewThreadInfo,
        setFlagRenderNewConversation,
        setSourceDetails,
        setSelectedThreadId,
        setSelectedThread,
        setSearchParams,
        handleError,
    ]);

    const setVoiceCallingData = useCallback((activeChat: any) => {
        const waba = getWabaNameAndPhoneNumberBytIntegratedChannelId(activeChat?.channel_name, activeChat?.integrated_channel_id);

        if (activeChat) {
            if (activeChat?.whatsapp_calling_data?.calling_enabled) {
                setIsVoiceCallingEnableWaba(true);
                setCallingWabaDetails(waba)
                setCallingPersonDetails(activeChat)
            }
            else {
                setIsVoiceCallingEnableWaba(false);
                setCallingWabaDetails(null)
                setCallingPersonDetails(null)
            }
        }
        else {
            setIsVoiceCallingEnableWaba(false);
            setCallingWabaDetails(null)
            setCallingPersonDetails(null)
        }

    }, [getWabaNameAndPhoneNumberBytIntegratedChannelId, setIsVoiceCallingEnableWaba, setCallingWabaDetails, setCallingPersonDetails])

    // Tracks the thread_id of the most recently requested thread (fresh load
    // only, not pagination).  When an API response arrives we compare its
    // request ID against this ref.  If they differ the user has already
    // navigated away, so we discard the response entirely — this prevents the
    // stale response from overwriting the chat the user is currently viewing.
    const activeRequestIdRef = useRef<string | null>(null);

    const getSpecificChat: GetSpecificChat = useCallback(async (
        chatbot_user_id,
        offSetValue,
        flagDebugParam,
        flagViewDetailedLogParam,
        flagGetThreadDetailByPhoneNumber = false,
        phoneNumberInfo = null,
        forceRefresh = false
    ) => {
        // Snapshot the request ID for this call.  We only update the "active"
        // ref for fresh thread loads (not pagination) so that switching threads
        // mid-flight correctly invalidates the in-flight request.
        const thisRequestId = chatbot_user_id.toString();
        if (offSetValue === 0) {
            activeRequestIdRef.current = thisRequestId;
        }

        // Read live thread for pagination merge so this callback does not need
        // to recreate on every socket message update.
        const liveActiveThread =
            useRuleBasedInboxStore.getState().currentActiveChatbotThread as currentActiveChatbotThreadTypeNew | null;

        // ── Cache-first path (initial load only, not pagination, not force-refresh and not debug mode) ──
        if (offSetValue === 0 && !forceRefresh && !flagDebugParam) {
            const cachedEntry = getThreadFromCache(thisRequestId);
            if (cachedEntry && cachedEntry.data?.chatbot_user_id?.toString() === thisRequestId) {
                // Restore sidebar source details from cached thread
                const activeChat = cachedEntry.data;
                const end_date = activeChat?.chatbot_thread_data?.[activeChat.chatbot_thread_data?.length - 1]?.timestamp ?? null;
                getSourceDetails(
                    getMetadataValue(activeChat?.thread_metadata, 'ip_address'), // activeChat?.chatbot_user_ip_address,
                    getMetadataValue(activeChat?.thread_metadata, 'chatbot_domain'),
                    activeChat?.chatbot_user_route_source,
                    formatPhoneNumber(getMetadataValue(activeChat?.thread_metadata, METADATA_KEYS['PHONE_NUMBER']) ?? '') ?? null,
                    getMetadataValue(activeChat?.thread_metadata, METADATA_KEYS['NAME']) ?? null,
                    end_date,
                    getMetadataValue(activeChat?.thread_metadata, 'chatbot_user_city'), // activeChat?.chatbot_user_city,
                    getMetadataValue(activeChat?.thread_metadata, 'chatbot_user_country'), // activeChat?.chatbot_user_country,
                    activeChat?.chatbot_user_input_token,
                    activeChat?.chatbot_user_output_token,
                    activeChat?.chatbot_data?.openai_assistant_id,
                    activeChat?.chatbot_data?.openai_thread_id,
                    getWabaNameAndPhoneNumberBytIntegratedChannelId(activeChat?.channel_name, activeChat?.integrated_channel_id)?.waba_name ?? null,
                    getWabaNameAndPhoneNumberBytIntegratedChannelId(activeChat?.channel_name, activeChat?.integrated_channel_id)?.phone_number ?? null,
                    activeChat?.chatbot_data?.flag_azure,
                    getInstaAccountNameAndIdByIntegratedChannelId(activeChat?.channel_name, activeChat?.integrated_channel_id)?.instagram_account_name ?? null,
                    getInstaAccountNameAndIdByIntegratedChannelId(activeChat?.channel_name, activeChat?.integrated_channel_id)?.instagram_account_id ?? null,
                    getMetadataValue(activeChat?.thread_metadata, METADATA_KEYS['NAME']) ?? null,
                    getThreadInstagramUserName(activeChat?.thread_metadata) ?? null,
                );

                // await checkIsVoiceCallingEnableWaba(activeChat);
                setVoiceCallingData(activeChat);

                // flushSync forces React to update the DOM synchronously.
                // Because the skeleton uses an early-return, #chatUI doesn't
                // exist until isLoadingChat becomes false. By flushing inside
                // the same JS task we can set scrollTop before the browser
                // ever paints — the user sees the correct position instantly,
                // with no top-to-bottom flash.
                flushSync(() => {
                    setCurrentActiveChatbotThread(cachedEntry.data);
                    setOffset(cachedEntry.offset);
                    setFlagHasMoreMessagesToLoad(cachedEntry.flagHasMoreMessages);
                    setIsLoadingChat(false);
                    setIsInfoTabLoading(false);
                });
                
                if (offSetValue === 0 && checkIfChatIsExpiredForLeaveBtn(getManualOverrideConfig(cachedEntry.data, userDetail?.user_assignment_id), false)) {
                    triggerAttchmentIconHeartbeat();
                }

                // Cancel any scroll-pin that was running for the previous thread
                scrollPinCleanupRef.current?.();
                scrollPinCleanupRef.current = null;

                // #chatUI is now in the DOM. Set scroll before first paint.
                const chatUI = document.getElementById("chatUI");
                if (chatUI) {
                    const savedPos = getThreadScrollPosition(chatbot_user_id.toString());

                    if (savedPos === null || savedPos === Infinity) {
                        // Either first visit OR user was at the bottom when they
                        // left → pin to bottom so late-loading images don't
                        // strand them mid-conversation.
                        scrollPinCleanupRef.current = keepScrollPinnedToBottom(chatUI);
                    } else {
                        // User had genuinely scrolled up → restore that exact
                        // position. No pinning needed; scroll anchoring handles
                        // any content above the viewport.
                        chatUI.scrollTop = savedPos;
                    }
                }
                return;
            }
        }

        // ── API fetch path ──
        if (offSetValue > 0) {
            setFlagLoadingPreviousMessages(true);
        } else {
            // Use skeleton loader for ruleBasedInbox instead of round loader
            if (flagGetThreadDetailByPhoneNumber) {
                setChatClicked(true);
            }
            setIsLoadingChat(true);
            setIsInfoTabLoading(true);
        }

        // Flag set to true when we detect a stale response mid-flight.
        // The finally block reads it to decide whether to update the UI.
        let isStaleRequest = false;

        try {
            let response: any;
            if (flagGetThreadDetailByPhoneNumber) {
                response = await agent.getThreadDetailsByPhoneNumber.get(
                    phoneNumberInfo?.integrated_channel_id ?? '',
                    phoneNumberInfo?.user_wa_phone_number ?? '',
                    String(flagDebugParam),
                    CHAT_MESSAGES_LIMIT,
                    offSetValue
                );
            } else {
                response = await agent.getThreadDetails.get(
                    chatbot_user_id.toString(),
                    String(flagDebugParam),
                    CHAT_MESSAGES_LIMIT,
                    offSetValue
                );
            }
            if (response.status && response.data) {
                const threadData = response.data;
                const currentChat = mapThreadExecutionToChatMessages(threadData.thread_execution_data, {
                    channelName: threadData.channel_name,
                    flagViewDetailedLog: flagViewDetailedLogParam,
                    flagNeedToShowDetailedLogBtn: needToShowViewDetailedLogBtn(userDetail),
                });

                // Check if we have more data to load
                const hasMoreData =
                    threadData?.pagination &&
                    Number(threadData?.pagination?.total_records || "0") > CHAT_MESSAGES_LIMIT + offSetValue;

                const temp = [
                    {
                        ...threadData,
                        chatbot_user_id: threadData.thread_id,
                        chatbot_thread_data: currentChat,
                        chatbot_user_route_source: threadData.channel_name,
                        manual_agent_data: threadData.manual_agent_data,
                        chatbot_user_active_flag: true,
                        chatbot_name: `${getIntegratedChannelData(
                            threadData.channel_name,
                            threadData.integrated_channel_id,
                            integrationListNewStructure,
                            threadData?.chatbot_data?.chatbot_name
                        )}`,
                    },
                ];

                const activeChat: any = temp[0];
                const end_date =
                    offSetValue === 0
                        ? activeChat?.chatbot_thread_data?.[activeChat.chatbot_thread_data?.length - 1]?.timestamp ?? null
                        : liveActiveThread?.chatbot_thread_data?.[liveActiveThread?.chatbot_thread_data?.length - 1]?.timestamp ?? null;

                // Build the updated current active chatbot thread
                let updated_current_active_chatbot_thread: any;
                if (offSetValue === 0) {
                    updated_current_active_chatbot_thread = activeChat;
                } else {
                    updated_current_active_chatbot_thread = {
                        ...liveActiveThread,
                        chatbot_thread_data: [...(currentChat ?? []), ...(liveActiveThread?.chatbot_thread_data ?? [])],
                        manual_agent_data: threadData.manual_agent_data,
                    };
                }

                // ── Persist to cache (covers both initial load and pagination) ──
                setThreadCache(thisRequestId, {
                    data: updated_current_active_chatbot_thread,
                    offset: offSetValue + CHAT_MESSAGES_LIMIT,
                    flagHasMoreMessages: hasMoreData,
                    cachedAt: Date.now(),
                });

                // Guard: if the user navigated to a different thread while
                // this request was in flight, discard the response entirely.
                // Setting isStaleRequest lets the finally block skip its UI
                // updates too (flushSync, keepScrollPinnedToBottom, etc.).

                // Update only the thread cache before checking the stale request condition. Update all other state only if the request is not stale.
                if (activeRequestIdRef.current !== thisRequestId) {
                    isStaleRequest = true;
                    return;
                }

                setCurrentActiveChatbotThread(updated_current_active_chatbot_thread);

                getSourceDetails(
                    getMetadataValue(activeChat?.thread_metadata, 'ip_address'), // activeChat.chatbot_user_ip_address,
                    getMetadataValue(activeChat?.thread_metadata, 'chatbot_domain'),
                    activeChat.chatbot_user_route_source,
                    formatPhoneNumber(getMetadataValue(activeChat?.thread_metadata, METADATA_KEYS['PHONE_NUMBER']) ?? '') ?? null,
                    getMetadataValue(activeChat?.thread_metadata, METADATA_KEYS['NAME']) ?? null,
                    end_date,
                    getMetadataValue(activeChat?.thread_metadata, 'chatbot_user_city'), // activeChat.chatbot_user_city,
                    getMetadataValue(activeChat?.thread_metadata, 'chatbot_user_country'), // activeChat.chatbot_user_country,
                    activeChat.chatbot_user_input_token,
                    activeChat.chatbot_user_output_token,
                    activeChat?.chatbot_data?.openai_assistant_id,
                    activeChat?.chatbot_data?.openai_thread_id,
                    getWabaNameAndPhoneNumberBytIntegratedChannelId(activeChat?.channel_name, activeChat?.integrated_channel_id)?.waba_name ?? null,
                    getWabaNameAndPhoneNumberBytIntegratedChannelId(activeChat?.channel_name, activeChat?.integrated_channel_id)?.phone_number ?? null,
                    activeChat?.chatbot_data?.flag_azure,
                    getInstaAccountNameAndIdByIntegratedChannelId(activeChat?.channel_name, activeChat?.integrated_channel_id)?.instagram_account_name ?? null,
                    getInstaAccountNameAndIdByIntegratedChannelId(activeChat?.channel_name, activeChat?.integrated_channel_id)?.instagram_account_id ?? null,
                    getMetadataValue(activeChat?.thread_metadata, METADATA_KEYS['NAME']) ?? null,
                    getThreadInstagramUserName(activeChat?.thread_metadata) ?? null,
                );

                // await checkIsVoiceCallingEnableWaba(activeChat)
                setVoiceCallingData(activeChat);

                // Update the offset value
                setOffset(offSetValue + CHAT_MESSAGES_LIMIT);

                // Update hasMoreMessagesToLoad state.
                setFlagHasMoreMessagesToLoad(hasMoreData);

                if (offSetValue === 0 && checkIfChatIsExpiredForLeaveBtn(getManualOverrideConfig(updated_current_active_chatbot_thread, userDetail?.user_assignment_id), false)) {
                    triggerAttchmentIconHeartbeat();
                }

                // Set Search Param and Selected Thread
                if (flagGetThreadDetailByPhoneNumber || !searchParams?.get('chatbot_user_id')) {
                    setSelectedThreadId(threadData?.thread_id);
                    setSelectedThread(activeChat);
                    setSearchParams(updateChatbotUserIdParam(threadData?.thread_id));
                }

                // scrollTop is set in the finally block after flushSync
            } else {
                if (flagGetThreadDetailByPhoneNumber) {
                    handleErrorForGetThreadDetailByPhoneNumber(response, phoneNumberInfo);
                } else {
                    handleError(response);
                }
            }
        } finally {
            // If the user navigated away while this request was in flight,
            // skip ALL UI updates.  The loading state was already cleared by
            // the cache-hit path of the thread the user switched to, so we
            // don't need to reset it here.
            if (isStaleRequest) return;

            // Always clear loading state, even on errors
            if (offSetValue > 0) {
                setFlagLoadingPreviousMessages(false);
            } else {
                // flushSync flushes ALL pending React state (including the
                // setCurrentActiveChatbotThread queued in the try block) and
                // the isLoadingChat update in the same synchronous pass.
                // #chatUI is therefore in the DOM when flushSync returns, so
                // we can set scrollTop before the browser paints — no flash.
                flushSync(() => {
                    setIsLoadingChat(false);
                    setIsInfoTabLoading(false);
                });

                // For an initial load always land at the very bottom and stay
                // there while images finish loading (keepScrollPinnedToBottom).
                if (offSetValue === 0) {
                    scrollPinCleanupRef.current?.();
                    const chatUI = document.getElementById("chatUI");
                    if (chatUI) {
                        scrollPinCleanupRef.current = keepScrollPinnedToBottom(chatUI);
                    }
                }
            }
        }
    }, [
        getThreadFromCache,
        setThreadCache,
        setFlagLoadingPreviousMessages,
        setIsLoadingChat,
        setIsInfoTabLoading,
        needToShowViewDetailedLogBtn,
        userDetail,
        setFlagHasMoreMessagesToLoad,
        setOffset,
        integrationListNewStructure,
        getSourceDetails,
        getWabaNameAndPhoneNumberBytIntegratedChannelId,
        getIntegratedChannelData,
        setCurrentActiveChatbotThread,
        checkIfChatIsExpiredForLeaveBtn,
        triggerAttchmentIconHeartbeat,
        getThreadScrollPosition,
        handleError,
        setVoiceCallingData,
        scrollPinCleanupRef,
        setChatClicked,
        handleErrorForGetThreadDetailByPhoneNumber,
        searchParams,
        setSelectedThreadId,
        setSelectedThread,
        setSearchParams,
    ]);

    return { getSpecificChat, activeRequestIdRef };
};
